/**
 * PastDueAlertsWidget — governor churn radar on the Dashboard.
 *
 * Surfaces tenants whose payment has failed (billing_status = past_due) as
 * proactive, in-app alerts so the CS/governor team can save the account before
 * it churns. Each row links to the tenant and can be dismissed once actioned.
 */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { governanceAPI } from '../../../lib/governanceApi';
import { useSuperAdminTheme } from '../../../contexts/SuperAdminThemeContext';
import { AlertTriangle, RefreshCw, ArrowRight, Check } from 'lucide-react';
import { toast } from 'sonner';

const POLL_MS = 60_000;

const fmtSince = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (mins < 60) return `${mins}m ago`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

export default function PastDueAlertsWidget() {
  const { theme } = useSuperAdminTheme();
  const isDark = theme === 'dark';
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dismissing, setDismissing] = useState(null);

  const fetchAlerts = useCallback(async () => {
    try {
      const res = await governanceAPI.getPastDueAlerts();
      setAlerts(res.data?.alerts || []);
    } catch (e) {
      console.warn('[PastDueAlerts] fetch failed', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAlerts();
    const t = setInterval(fetchAlerts, POLL_MS);
    return () => clearInterval(t);
  }, [fetchAlerts]);

  const dismiss = async (e, alertId) => {
    e.stopPropagation();
    setDismissing(alertId);
    try {
      await governanceAPI.resolvePastDueAlert(alertId);
      setAlerts((prev) => prev.filter((a) => a.id !== alertId));
      toast.success('Alert dismissed');
    } catch (_e) {
      toast.error('Could not dismiss alert');
    } finally {
      setDismissing(null);
    }
  };

  const cardBg = isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200';
  const total = alerts.length;
  const accent = total === 0
    ? (isDark ? 'text-emerald-400' : 'text-emerald-600')
    : (isDark ? 'text-rose-400' : 'text-rose-600');

  return (
    <div className={`rounded-xl border p-5 ${cardBg}`} data-testid="past-due-alerts-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <AlertTriangle className={`w-5 h-5 ${accent}`} />
          <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Past-due Alerts
          </h3>
          {total > 0 && (
            <span
              className={`text-xs px-2 py-0.5 rounded-full font-semibold ${isDark ? 'bg-rose-500/20 text-rose-300' : 'bg-rose-100 text-rose-700'}`}
              data-testid="past-due-alerts-count"
            >
              {total}
            </span>
          )}
        </div>
        <button
          onClick={fetchAlerts}
          className={`p-1.5 rounded-md transition ${isDark ? 'text-slate-400 hover:text-white hover:bg-slate-800' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}
          data-testid="past-due-alerts-refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {loading && total === 0 ? (
        <div className={`text-sm ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Loading…</div>
      ) : total === 0 ? (
        <div className={`text-sm ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} data-testid="past-due-alerts-empty">
          No past-due tenants. Revenue is healthy.
        </div>
      ) : (
        <div className="space-y-2">
          {alerts.slice(0, 8).map((a) => (
            <div
              key={a.id}
              onClick={() => navigate(`/governance/tenants/${a.tenant_id}`)}
              className={`w-full flex items-center justify-between text-left px-3 py-2 rounded-md border cursor-pointer transition ${isDark ? 'border-slate-800 hover:border-rose-700 hover:bg-rose-950/20' : 'border-slate-200 hover:border-rose-400 hover:bg-rose-50'}`}
              data-testid={`past-due-alert-${a.tenant_id}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <AlertTriangle className={`w-4 h-4 ${accent} shrink-0`} />
                <div className="min-w-0">
                  <div className={`text-sm font-medium truncate ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>
                    {a.tenant_name} · <span className="uppercase">{a.plan_id}</span>
                  </div>
                  <div className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-600'}`}>
                    {a.org_name} · {fmtSince(a.created_at)}{a.contact_email ? ` · ${a.contact_email}` : ''}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={(e) => dismiss(e, a.id)}
                  disabled={dismissing === a.id}
                  title="Dismiss (actioned)"
                  className={`p-1 rounded-md transition disabled:opacity-50 ${isDark ? 'text-slate-400 hover:text-emerald-400 hover:bg-slate-800' : 'text-slate-500 hover:text-emerald-600 hover:bg-slate-100'}`}
                  data-testid={`past-due-dismiss-${a.tenant_id}`}
                >
                  <Check className="w-4 h-4" />
                </button>
                <ArrowRight className={`w-4 h-4 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
